import { MarkdownText } from './markdown-text';
import { ReactNode, useEffect, useId, useRef, useState } from "react";
import { TabView, TabPanel } from "primereact/tabview";
import { SelectButton } from "primereact/selectbutton";
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { ProgressSpinner } from "primereact/progressspinner";
import { LearningItemDto, LearningPathDto } from "../general/dtos";
import { GeneralInfoDialog } from "./dialogs/info-dialog";
import '../assets/learning-path.css';
import { LearningOverview } from './learning-overview';

import { EstimatedDuration } from './estimated-duration';
import { LearningActivity } from './learning-activity';
import { ModuleTypeIcon } from './module-type-icon';
import { YourCertificates } from './your-certificates';
import { LearningProgressTree } from './learning-progress-tree';
import '../assets/learning-home.css';

type LearningPathViewerProps = {
  learningPath: LearningPathDto | null;
  loading: boolean;
  enableViewButton: boolean;
  onView: (item: LearningItemDto, startFromBeginning?: boolean) => void;
  isMobile: boolean;
  isImpersonating: boolean;
  showDashboard?: boolean;
  learnerName?: string;
};

function savedLearningPreference(key: string): string | null {
  try { return localStorage.getItem(key); } catch { return null; }
}
function saveLearningPreference(key: string, value: string) {
  try { localStorage.setItem(key, value); } catch { /* Preferences are optional. */ }
}

export function LearningPathViewer({
  learningPath,
  loading,
  enableViewButton,
  onView,
  isImpersonating,
  showDashboard = true,
  learnerName
}: LearningPathViewerProps) {
  
  const [infoVisible, setInfoVisible] = useState(false);
  const [lockedItem, setLockedItem] = useState<LearningItemDto | null>(null);
  const [view, setView] = useState(() => savedLearningPreference('triad.learning-view') === 'path' ? 'path' : 'cards');
  const [selectedCategory, setSelectedCategory] = useState<number | null>(() => {
    const saved = savedLearningPreference('triad.learning-category');
    return saved !== null && /^[0-3]$/.test(saved) ? Number(saved) : null;
  });
  const lessonsSection = useRef<HTMLDivElement>(null);
  const changeView = (value: string) => {
    setView(value);
    saveLearningPreference('triad.learning-view', value);
  };
  const selectCategory = (index: number) => {
    setSelectedCategory(index);
    saveLearningPreference('triad.learning-category', String(index));
    changeView('cards');
    requestAnimationFrame(() => {
      lessonsSection.current?.scrollIntoView?.({
        behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start'
      });
    });
  };

  const showLockedInfo = (item: LearningItemDto) => {
    setLockedItem(item);
    setInfoVisible(true);
  };

  if (loading) {
    return (
      <div className="flex align-items-center gap-2 mt-4">
        <ProgressSpinner style={{ width: "30px", height: "30px" }} />
        <span className="text-sm text-secondary">{isImpersonating ? 'Loading learning path…' : 'Loading your assignments…'}</span>
      </div>
    );
  }

  if (
    !learningPath ||
    !Array.isArray(learningPath.available) ||
    !Array.isArray(learningPath.inProgress) ||
    !Array.isArray(learningPath.locked) ||
    !Array.isArray(learningPath.completed)
  ) {
    return null;
  }

  const activeCategory = selectedCategory ?? (learningPath.inProgress.length ? 1 : 0);
  const personalHome = showDashboard && !isImpersonating;
  const startedCount = new Set(learningPath.inProgress.map(item => item.key)).size;
  const welcome = learningPath.inProgress.length || learningPath.completed.length ? 'Welcome back' : 'Welcome';
  const welcomeMessage = startedCount ? `You have ${startedCount === 1 ? 'one lesson' : `${startedCount} lessons`} in progress. Pick up where you left off.`
    : learningPath.available.length ? 'Your next lesson is ready when you are.'
    : learningPath.locked.length ? 'Explore your learning path to see what comes next.'
    : learningPath.completed.length ? 'You’re all caught up. Revisit a lesson or enjoy what you’ve achieved.'
    : 'Make yourself at home. Your lessons will appear here when they’re assigned.';
  const categories = [
    { label: 'Ready to start', icon: 'pi-play', items: learningPath.available, category: 'ready' as const,
      description: 'Choose a lesson and take your next step.', empty: 'No lessons ready to start.' },
    { label: 'In progress', icon: 'pi-bookmark', items: learningPath.inProgress, category: 'started' as const,
      description: 'Pick up where you left off, or start again whenever you like.', empty: 'No lessons in progress yet.' },
    { label: 'Locked', icon: 'pi-lock', items: learningPath.locked, category: 'waiting' as const,
      description: 'Complete the prerequisites to unlock these lessons.', empty: 'No lessons waiting on prerequisites.' },
    { label: 'Completed', icon: 'pi-check-circle', items: learningPath.completed, category: 'completed' as const,
      description: 'Your achievements so far. Revisit any lesson to refresh your knowledge.', empty: 'Your completed lessons will appear here.' },
  ];

  // ------------------------------------------------------------
  // Helpers
  // ------------------------------------------------------------
  const sortByOrderIndexThenTitle = <T extends { orderIndex?: number; title: string }>(
    items: T[]
  ): T[] => {
    return [...items].sort((a, b) => {
      const ai = a.orderIndex ?? Number.POSITIVE_INFINITY;
      const bi = b.orderIndex ?? Number.POSITIVE_INFINITY;
      if (ai !== bi) return ai - bi;
      return a.title.localeCompare(b.title, undefined, { sensitivity: "base" });
    });
  };

  type LessonCategory = 'ready' | 'started' | 'waiting' | 'completed';
  const actionLabels: Record<LessonCategory, string> = {
    ready: 'Start lesson', started: 'Open lesson again', waiting: 'View prerequisites', completed: 'Review lesson'
  };
  const renderActionButton = (item: LearningItemDto, category: LessonCategory) => {
    if (category === 'waiting') {
      return (
        <Button
          label="View prerequisites"
          aria-label={`View prerequisites for ${item.title}`}
          icon="pi pi-lock"
          className="p-button-text p-button-sm"
          onClick={() => showLockedInfo(item)}
        />
      );
    }

    if (!enableViewButton) return null;

    const startedManaged = category === 'started' && item.is_managed;
    const canResume = startedManaged && item.can_resume === true;
    const actionLabel = startedManaged
      ? canResume ? 'Continue learning' : 'Start from beginning' : actionLabels[category];
    return <div className="flex flex-wrap gap-1">
      <Button
        label={actionLabel}
        aria-label={`${actionLabel}: ${item.title}`}
        tooltip={startedManaged ? canResume
          ? 'Resume at your last saved page. Your saved answers are kept.'
          : 'Open the first page of the lesson. Your saved answers are kept.' : undefined}
        tooltipOptions={{ position: 'top', event: 'both' }}
        icon="pi pi-eye"
        className={category === 'completed' ? 'p-button-text p-button-sm' : 'p-button-sm'}
        onClick={() => startedManaged && !canResume ? onView(item, true) : onView(item)}
      />
      {canResume && <Button
        label="Start from beginning"
        aria-label={`Start from beginning: ${item.title}`}
        tooltip="Open the first page of the lesson. Your saved answers are kept."
        tooltipOptions={{ position: 'top', event: 'both' }}
        icon="pi pi-replay"
        className="p-button-text p-button-sm"
        onClick={() => onView(item, true)}
      />}
    </div>;
  };

  const renderLessonTitle = (item: LearningItemDto) => (
    <span className="lesson-title font-medium">
      <ModuleTypeIcon managed={item.is_managed} />
      <span>{item.title}</span>
    </span>
  );

  const renderCourseContext = (item: LearningItemDto) => (
    <div className="lesson-course-context">
      {item.parentCourses?.length ? <>
        <span className="lesson-course-label">{item.parentCourses.length === 1 ? 'Course:' : 'Courses:'}</span>
        {item.parentCourses.map(course => <span key={course.key} className="lesson-course-badge">
          <i className="pi pi-book" aria-hidden="true" />{course.title}
        </span>)}
      </> : <span className="lesson-course-label">Standalone lesson</span>}
    </div>
  );

  const renderList = (items: LearningItemDto[], category: LessonCategory, emptyMessage: string) => {
    if (items.length === 0) {
      return <span className="text-sm text-color-secondary">{emptyMessage}</span>;
    }

    const sorted = sortByOrderIndexThenTitle(items);

    return (
      <div className={`lesson-card-grid lesson-cards-${category}`}>
        {sorted.map(item => <LessonCard
          key={JSON.stringify([item.key, item.description])}
          title={item.title}
          description={item.description}
          heading={<div className="lesson-details">{renderLessonTitle(item)}{renderCourseContext(item)}<EstimatedDuration minutes={item.estimated_minutes} /></div>}
          actions={renderActionButton(item, category)}
        />)}
      </div>
    );
  };

  return (
    <>
      <div className="mt-0 learning-home">
        <div className="learning-home-toolbar" ref={lessonsSection}>
          <div className={personalHome ? 'learning-home-welcome' : undefined}>
            <h3 className={isImpersonating ? '' : 'public-page-title'}>{personalHome ? `${welcome}${learnerName?.trim() ? `, ${learnerName.trim()}` : ''}` : isImpersonating ? 'Learning path' : 'Your lessons'}</h3>
            {personalHome && <p>{welcomeMessage}</p>}
          </div>
          <SelectButton aria-label="Lesson view" value={view} allowEmpty={false}
            options={[{ label: 'Cards', value: 'cards', icon: 'pi pi-th-large' }, { label: 'Learning path', value: 'path', icon: 'pi pi-sitemap' }]}
            itemTemplate={option => <span className="learning-view-option"><i className={option.icon} aria-hidden="true" />{option.label}</span>}
            onChange={event => { if (event.value) changeView(event.value); }} />
        </div>
        {view === 'cards' ? <TabView className="learning-status-tabs" scrollable activeIndex={activeCategory}
          onTabChange={event => { setSelectedCategory(event.index); saveLearningPreference('triad.learning-category', String(event.index)); }}>
          {categories.map(group => <TabPanel key={group.category} header={<span className="learning-tab-label">
            <i className={`pi ${group.icon}`} aria-hidden="true" /><span>{group.label}</span><span className="learning-tab-count">{group.items.length}</span>
          </span>}>
            <div className="lesson-category">
              <p className="learning-category-intro">{group.description}</p>
              {group.items.length ? renderList(group.items, group.category, group.empty)
                : <div className="learning-category-empty"><i className={`pi ${group.icon}`} aria-hidden="true" /><p>{group.empty}</p></div>}
            </div>
          </TabPanel>)}
        </TabView> : <LearningProgressTree learningPath={learningPath} enableViewButton={enableViewButton} onView={onView} roadmap
          renderActions={(item, status) => renderActionButton(item, status === 'locked' ? 'waiting' : status)} />}
        {showDashboard && <div className="learning-home-dashboard">
          <LearningOverview learningPath={learningPath} onSelectCategory={selectCategory} />
          {!isImpersonating && <div className="learning-home-bottom">
            <LearningActivity learningPath={learningPath} />
            <YourCertificates learningPath={learningPath} />
          </div>}
        </div>}
      </div>

      <GeneralInfoDialog
            visible={infoVisible}
            title={`Prerequisites for "${lockedItem?.title ?? ''}"`}
            message=""
            onClose={() => setInfoVisible(false)}
      >
        <p>You cannot start this lesson yet because the following prerequisites are not completed:</p>
        <ul>{(lockedItem?.unmetPrerequisites ?? []).map(prerequisite => <li key={`${prerequisite.isCourse}-${prerequisite.key}`}>
          <strong>{prerequisite.isCourse ? 'Course' : 'Lesson'}:</strong> {prerequisite.title}
        </li>)}</ul>
      </GeneralInfoDialog>
    </>
  );
}


function LessonCard({ description, title, heading, actions }: {
  description?: string | null; title: string; heading: ReactNode; actions: ReactNode;
}) {
  const text = useRef<HTMLDivElement>(null);
  const id = useId();
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);
  useEffect(() => {
    if (expanded || !text.current) return;
    const element = text.current;
    const measure = () => setOverflows(element.scrollHeight > element.clientHeight + 1);
    measure();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    observer?.observe(element);
    window.addEventListener('resize', measure);
    return () => { observer?.disconnect(); window.removeEventListener('resize', measure); };
  }, [expanded]);
  const toggle = overflows && <Button label={expanded ? 'Show less' : 'Show more'} text size="small" className="lesson-description-toggle"
      aria-label={`${expanded ? 'Show less' : 'Show more'} description: ${title}`} aria-expanded={expanded} aria-controls={id}
      onClick={() => setExpanded(value => !value)} />;
  return <Card className="lesson-card" title={heading}
    footer={(actions || toggle) && <div className="lesson-card-actions">{actions}{toggle}</div>}>
    {description && <MarkdownText ref={text} id={id} className={`lesson-description-text text-sm text-color-secondary${expanded ? '' : ' lesson-description-preview'}`} text={description} />}
  </Card>;
}
